import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';
import { addVariantToCart, closeAllPopups } from '@theme/quick-add-cart';

const LOAD_MORE_LABEL = 'Load more products';
const LOADING_LABEL = 'Loading...';
const PLP_NON_NAVIGABLE_SELECTOR =
  '[data-plp-no-nav], product-price, .product-item-price, .product-promo-text-tag, .text-block.product-promo-text-tag';

function isCollectionPlpTemplate() {
  const template = document.querySelector('main#MainContent')?.dataset.template || '';
  return template.includes('collection');
}

/**
 * @param {Element} card
 * @param {number} clientX
 * @param {number} clientY
 */
function isPointOverPlpNonNavigable(card, clientX, clientY) {
  const areas = card.querySelectorAll(PLP_NON_NAVIGABLE_SELECTOR);

  for (const area of areas) {
    if (!(area instanceof Element)) continue;

    const rect = area.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) continue;

    if (
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom
    ) {
      return true;
    }
  }

  return false;
}

/**
 * @param {EventTarget | null} target
 * @param {Event} event
 */
function isPlpNonNavigableTarget(target, event) {
  if (!(target instanceof Element) || !isCollectionPlpTemplate()) return false;
  if (!target.closest('.evry-plp')) return false;

  const card = target.closest('product-card');
  if (!card) return false;

  if (target.closest(PLP_NON_NAVIGABLE_SELECTOR)) return true;

  let clientX = 0;
  let clientY = 0;

  if (event instanceof MouseEvent) {
    clientX = event.clientX;
    clientY = event.clientY;
  } else if (event instanceof TouchEvent) {
    const touch = event.changedTouches[0] || event.touches[0];
    if (touch) {
      clientX = touch.clientX;
      clientY = touch.clientY;
    }
  }

  return isPointOverPlpNonNavigable(card, clientX, clientY);
}

/**
 * @param {ParentNode} root
 */
function getPlpRoot(root = document) {
  return root.querySelector('.evry-plp');
}

/**
 * @param {ParentNode} root
 */
function getProductGrid(root = document) {
  const plp = getPlpRoot(root);
  return plp?.querySelector('.product-grid') ?? null;
}

/**
 * @param {ParentNode} root
 */
function getLoadMoreContainer(root = document) {
  const plp = getPlpRoot(root);
  return plp?.querySelector('.constructor-load-more') ?? null;
}

/**
 * @param {HTMLElement} grid
 */
function countRenderedProducts(grid) {
  return grid.querySelectorAll(':scope > li.product-grid__item[data-product-id]').length;
}

/**
 * @param {HTMLElement} container
 * @param {number} shown
 * @param {number} total
 */
function updatePaginationUI(container, shown, total) {
  const message = container.querySelector('.collection-results-message');
  const fill = container.querySelector('.plp-pagination__fill');

  if (message) {
    message.textContent = `Showing ${shown} of ${total} products`;
  }

  if (fill) {
    const percent = total > 0 ? Math.min(100, (shown / total) * 100) : 0;
    fill.style.width = shown > 0 ? `${percent}%` : '0%';
  }
}

/**
 * @param {ParentNode} root
 */
function syncPaginationFromGrid(root = document) {
  const grid = getProductGrid(root);
  const container = getLoadMoreContainer(root);
  if (!grid || !container) return;

  const total = Number.parseInt(container.dataset.totalProducts || '0', 10) || 0;
  const shown = countRenderedProducts(grid);
  updatePaginationUI(container, shown, total);
}

/**
 * @param {HTMLElement} cardRoot
 * @param {{ mediaId?: string, imageUrl?: string, imageSrcset?: string }} payload
 */
function updateGalleryImage(cardRoot, payload) {
  const { mediaId, imageUrl, imageSrcset } = payload;
  const gallery = cardRoot.querySelector('.card-gallery');
  if (!gallery) return false;

  const slideshowEl = gallery.querySelector('slideshow-component');
  let slideActivated = false;

  if (slideshowEl && mediaId && typeof slideshowEl.select === 'function') {
    const targetSlide = gallery.querySelector(`slideshow-slide[slide-id="${mediaId}"]`);

    if (targetSlide) {
      slideshowEl.select({ id: mediaId }, undefined, { animate: false });
      slideActivated = true;
    }
  }

  if (mediaId) {
    const targetSlide = gallery.querySelector(`slideshow-slide[slide-id="${mediaId}"]`);

    if (targetSlide) {
      gallery.querySelectorAll('slideshow-slide').forEach((slide) => {
        const isTarget = slide === targetSlide;
        slide.setAttribute('aria-hidden', isTarget ? 'false' : 'true');

        if (isTarget && slide.hasAttribute('hidden')) {
          slide.setAttribute('reveal', '');
        }
      });
      slideActivated = true;
    }
  }

  const visibleSlide =
    (mediaId ? gallery.querySelector(`slideshow-slide[slide-id="${mediaId}"]`) : null) ||
    gallery.querySelector('slideshow-slide[aria-hidden="false"]') ||
    gallery.querySelector('slideshow-slide:first-of-type');

  if (imageUrl && visibleSlide) {
    visibleSlide.querySelectorAll('img').forEach((img) => {
      if (imageSrcset) {
        img.srcset = imageSrcset;
      } else {
        img.removeAttribute('srcset');
      }

      img.src = imageUrl;
      img.removeAttribute('loading');
    });
    slideActivated = true;
  }

  return slideActivated;
}

/**
 * Pin each PLP gallery to its primary slide so slideshow.js cannot flip aria-hidden.
 * @param {ParentNode} root
 */
function stabilizePlpGalleries(root = document) {
  const plp = getPlpRoot(root);
  if (!plp) return;

  plp.querySelectorAll('.card-gallery--plp').forEach((gallery) => {
    const slides = Array.from(gallery.querySelectorAll('slideshow-slide')).filter(
      (slide) => !slide.hasAttribute('hidden')
    );
    if (!slides.length) return;

    const primary =
      slides.find((slide) => slide.getAttribute('aria-hidden') === 'false') || slides[0];

    gallery.querySelectorAll('slideshow-slide').forEach((slide) => {
      const isPrimary = slide === primary;
      slide.setAttribute('aria-hidden', isPrimary ? 'false' : 'true');
      if (isPrimary) {
        slide.removeAttribute('hidden');
      }
    });
  });
}

/**
 * @param {HTMLElement} swatchBtn
 */
function handleSwatchClick(swatchBtn) {
  const imageUrl = swatchBtn.dataset.imageUrl;
  const imageSrcset = swatchBtn.dataset.imageSrcset;
  const mediaId = swatchBtn.dataset.mediaId;

  if (!imageUrl && !mediaId) return;

  if (swatchBtn.classList.contains('plp-swatch--active')) return;

  const cardRoot =
    swatchBtn.closest('product-card') ||
    swatchBtn.closest('.product-grid__item') ||
    swatchBtn.closest('.product-grid__card');

  if (!cardRoot) return;

  const swatchList = swatchBtn.closest('[data-plp-swatches]');
  swatchList?.querySelectorAll('[data-plp-swatch]').forEach((btn) => {
    btn.classList.remove('plp-swatch--active');
  });
  swatchBtn.classList.add('plp-swatch--active');

  const imageContainer = cardRoot.querySelector('.plp-media-stack__image');

  function applyImage() {
    updateGalleryImage(cardRoot, { mediaId, imageUrl, imageSrcset });
  }

  if (window.EvryVariantBlink && window.EvryVariantBlink.swap) {
    window.EvryVariantBlink.swap({
      container: imageContainer,
      imageUrl: imageUrl,
      applyImage: applyImage,
    });
    return;
  }

  applyImage();
}

/**
 * @param {HTMLElement} button
 */
async function handleLoadMore(button) {
  const container = button.closest('.constructor-load-more');
  const resultsList = button.closest('results-list');
  const grid = getProductGrid(document);

  if (!container || !resultsList || !grid) return;

  const nextPage = Number.parseInt(button.dataset.nextPage || '0', 10);
  const sectionId = resultsList.getAttribute('section-id');
  const lastPage = Number.parseInt(grid.dataset.lastPage || '0', 10);

  if (!sectionId || !nextPage || nextPage > lastPage) return;

  const originalHtml = button.innerHTML;
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  const label = button.querySelector('span');
  if (label) {
    label.textContent = LOADING_LABEL;
  }

  try {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(nextPage));
    url.hash = '';

    const sectionHTML = await sectionRenderer.getSectionHTML(sectionId, true, url);
    const doc = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const parsedGrid = doc.querySelector('[ref="grid"]');

    if (!parsedGrid) {
      throw new Error('Product grid not found in section response');
    }

    const existingIds = new Set(
      Array.from(grid.querySelectorAll(':scope > li.product-grid__item[data-product-id]')).map((item) =>
        item.getAttribute('data-product-id')
      )
    );

    const newItems = Array.from(parsedGrid.querySelectorAll(':scope > [ref="cards[]"]')).filter((item) => {
      const productId = item.getAttribute('data-product-id');
      return productId && !existingIds.has(productId);
    });

    if (newItems.length) {
      grid.append(...newItems);
      window.requestAnimationFrame(() => {
        stabilizePlpGalleries();
      });
    }

    const parsedPagination = doc.querySelector('.constructor-load-more');
    const totalProducts = Number.parseInt(parsedPagination?.dataset.totalProducts || container.dataset.totalProducts || '0', 10);

    if (totalProducts > 0) {
      container.dataset.totalProducts = String(totalProducts);
    }

    const shown = countRenderedProducts(grid);
    updatePaginationUI(container, shown, totalProducts);

    const loadMoreFromResponse = parsedPagination?.querySelector('[data-action="load-more"]');

    if (loadMoreFromResponse instanceof HTMLButtonElement) {
      button.dataset.nextPage = loadMoreFromResponse.dataset.nextPage || '';
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.innerHTML = originalHtml;
    } else {
      button.remove();
    }
  } catch (error) {
    console.error('PLP load more error:', error);
    button.disabled = false;
    button.removeAttribute('aria-busy');
    button.innerHTML = originalHtml;
  }
}

function setPlpVariantPanelState(popup, isOpen) {
  document.querySelectorAll('.plp-variant-panel-open').forEach((el) => {
    el.classList.remove('plp-variant-panel-open');
  });

  if (!isOpen || !(popup instanceof HTMLElement)) return;

  popup.closest('.plp-media-stack')?.classList.add('plp-variant-panel-open');
  popup.closest('.card-gallery--plp')?.classList.add('plp-variant-panel-open');
  popup.closest('.product-grid__item')?.classList.add('plp-variant-panel-open');
}

/**
 * @param {HTMLElement} popup
 */
function openPlpVariantPopup(popup) {
  popup.hidden = false;
  popup.style.removeProperty('display');
  setPlpVariantPanelState(popup, true);
}

/**
 * @param {HTMLElement | null | undefined} wrapper
 */
function closePlpVariantPopup(wrapper) {
  const popup = wrapper?.querySelector('.variant-popup');
  if (popup instanceof HTMLElement) {
    popup.hidden = true;
    popup.style.removeProperty('display');
  }

  wrapper?.closest('.plp-media-stack')?.classList.remove('plp-variant-panel-open');
  wrapper?.closest('.card-gallery--plp')?.classList.remove('plp-variant-panel-open');
  wrapper?.closest('.product-grid__item')?.classList.remove('plp-variant-panel-open');
}

function isMobilePlpViewport() {
  return window.matchMedia('(max-width: 749px)').matches;
}

const PLP_TAP_MOVE_THRESHOLD_PX = 10;

/**
 * @param {Element | null | undefined} target
 */
function getPlpCartActionElement(target) {
  if (!(target instanceof Element)) return null;

  return (
    target.closest('.evry-plp [data-open-popup]') ||
    target.closest('.evry-plp [data-single-add-to-cart]') ||
    target.closest('.evry-plp [data-add-to-cart]')
  );
}

function bindPlpCollectionEvents() {
  if (document.body.dataset.plpCollectionBound === 'true') return;
  document.body.dataset.plpCollectionBound = 'true';

  let suppressClickUntil = 0;
  /** @type {{ x: number, y: number, scrolled: boolean } | null} */
  let plpTouch = null;

  /**
   * @param {Touch} touch
   */
  function markPlpTouchScroll(touch) {
    if (!plpTouch || plpTouch.scrolled) return;

    const dx = touch.clientX - plpTouch.x;
    const dy = touch.clientY - plpTouch.y;

    if (dx * dx + dy * dy > PLP_TAP_MOVE_THRESHOLD_PX * PLP_TAP_MOVE_THRESHOLD_PX) {
      plpTouch.scrolled = true;
    }
  }

  /**
   * @param {Element | null} target
   * @param {Event} event
   */
  function handlePlpAction(target, event) {
    if (!(target instanceof Element)) return;

    if (event.type === 'click') {
      if (Date.now() < suppressClickUntil) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (isMobilePlpViewport() && plpTouch?.scrolled) {
        event.preventDefault();
        event.stopPropagation();
        plpTouch = null;
        return;
      }
    }

    const loadMoreBtn = target.closest('[data-action="load-more"]');
    if (loadMoreBtn instanceof HTMLButtonElement && loadMoreBtn.closest('.evry-plp')) {
      event.preventDefault();
      handleLoadMore(loadMoreBtn);
      return;
    }

    const swatchBtn = target.closest('[data-plp-swatch]');
    if (swatchBtn instanceof HTMLButtonElement && swatchBtn.closest('.evry-plp')) {
      event.preventDefault();
      event.stopPropagation();
      handleSwatchClick(swatchBtn);
      return;
    }

    const openBtn = target.closest('.evry-plp [data-open-popup]');
    if (openBtn instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopPropagation();

      const wrapper = openBtn.closest('.quick-add-wrapper');
      const popup = wrapper?.querySelector('.variant-popup');
      if (!popup) return;

      const wasOpen = !popup.hidden;
      closeAllPopups();

      if (isMobilePlpViewport() || !wasOpen) {
        openPlpVariantPopup(popup);
      }
      return;
    }

    const singleBtn = target.closest('.evry-plp [data-single-add-to-cart]');
    if (singleBtn instanceof HTMLButtonElement && !singleBtn.disabled) {
      event.preventDefault();
      event.stopPropagation();

      const variantId = singleBtn.dataset.variantId;
      if (!variantId) return;

      void addVariantToCart(variantId, { button: singleBtn });
      return;
    }

    const addBtn = target.closest('.evry-plp [data-add-to-cart]');
    if (addBtn instanceof HTMLButtonElement) {
      event.preventDefault();
      event.stopPropagation();

      const variantId = addBtn.dataset.variantId;
      if (!variantId) return;

      const wrapper = addBtn.closest('.quick-add-wrapper');
      const mainAtcBtn = wrapper?.querySelector('[data-open-popup], [data-single-add-to-cart]');

      closePlpVariantPopup(wrapper instanceof HTMLElement ? wrapper : null);

      void addVariantToCart(variantId, {
        button: mainAtcBtn instanceof HTMLButtonElement ? mainAtcBtn : addBtn,
        closePopups: isMobilePlpViewport(),
      });
      return;
    }

    if (!target.closest('.evry-plp .quick-add-wrapper')) {
      closeAllPopups();
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      if (isPlpNonNavigableTarget(event.target, event)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      handlePlpAction(event.target, event);
    },
    true
  );

  document.addEventListener(
    'touchend',
    (event) => {
      if (!isPlpNonNavigableTarget(event.target, event)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
    },
    { capture: true, passive: false }
  );

  document.addEventListener(
    'touchstart',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element) || !getPlpCartActionElement(target)) {
        plpTouch = null;
        return;
      }

      const touch = event.changedTouches[0];
      if (!touch) return;

      plpTouch = {
        x: touch.clientX,
        y: touch.clientY,
        scrolled: false,
      };
    },
    { capture: true, passive: true }
  );

  document.addEventListener(
    'touchend',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const touch = event.changedTouches[0];
      if (!touch) return;

      markPlpTouchScroll(touch);

      if (!getPlpCartActionElement(target)) {
        plpTouch = null;
        return;
      }

      if (!plpTouch || plpTouch.scrolled) {
        if (plpTouch?.scrolled) {
          suppressClickUntil = Date.now() + 500;
        }
        plpTouch = null;
        return;
      }

      plpTouch = null;
      handlePlpAction(target, event);
      suppressClickUntil = Date.now() + 500;
    },
    { capture: true }
  );

  document.addEventListener(
    'touchcancel',
    () => {
      plpTouch = null;
    },
    { capture: true }
  );

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeAllPopups();
    }
  });

  document.addEventListener(
    'touchmove',
    (event) => {
      const touch = event.touches[0];
      if (touch) {
        markPlpTouchScroll(touch);
      }

      const target = event.target;
      if (!(target instanceof Element)) return;
      if (
        target.closest('.evry-plp .plp-variant-dropdown .variant-list') ||
        target.closest('.evry-plp .plp-media-stack .plp-variant-dropdown:not([hidden])')
      ) {
        event.stopPropagation();
      }
    },
    { capture: true, passive: true }
  );

  document.addEventListener(ThemeEvents.FilterUpdate, () => {
    window.requestAnimationFrame(() => {
      syncPaginationFromGrid();
      stabilizePlpGalleries();
    });
  });
}

function initPlpCollection() {
  const plp = getPlpRoot();
  if (!plp) return;

  bindPlpCollectionEvents();
  syncPaginationFromGrid();
  stabilizePlpGalleries();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPlpCollection, { once: true });
} else {
  initPlpCollection();
}

export { initPlpCollection, syncPaginationFromGrid };
